import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";

// Button that lets Master Admin / CRM Manager / Sales Admin ask the assigned
// associate to call the lead and update the remark. The request lands in the
// associate's Dashboard widget; if not acted on within 3 days, the admin
// dashboard surfaces it for override.
export default function LeadCallRequestButton({ clientId, clientName, assignedToName, source = "crm_reporting", size = "sm", variant = "outline", className = "" }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/lead-call-requests", { client_id: clientId, note: note.trim(), source });
      toast.success(`Call request sent to ${assignedToName || "associate"}`);
      setOpen(false);
      setNote("");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!assignedToName) {
    return (
      <Button
        size={size}
        variant={variant}
        disabled
        className={`h-8 text-[11px] opacity-60 cursor-not-allowed ${className}`}
        title="Client has no assigned associate"
        data-testid={`lcr-disabled-${clientId}`}
      >
        <Phone size={11} className="mr-1" /> No Associate
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        className={`h-8 text-[11px] bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 ${className}`}
        data-testid={`lcr-trigger-${clientId}`}
      >
        <Phone size={11} className="mr-1" /> Request Update
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading">Request Call Update</DialogTitle>
            <div className="text-[11px] text-slate-500">
              We&apos;ll ping <span className="font-semibold">{assignedToName}</span> to call
              {clientName ? <> <span className="font-semibold">{clientName}</span></> : <> this lead</>} and update the log. 3-day SLA — overdue requests surface on the admin dashboard.
            </div>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Note (optional)</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Please confirm the demo date and renewal status."
              data-testid="lcr-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="lcr-submit">
              {busy ? "Sending…" : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
